/* BEDROCK promotion banner — the <bedrock-countdown> element and the discount-code
   copy button. Both are section-local; nothing else in the theme uses them.

   Two modes, mirroring the reference:
   - evergreen: the target is recomputed from the viewer's own clock (next month's
     1st, next Sunday, or the next N-hour boundary of the day), so it rolls over
     instead of expiring. Recomputing on rollover is what keeps a page left open
     overnight from freezing at 00:00:00:00.
   - fixed: a real end date. Once it passes the timer removes itself.

   The markup ships with "00" placeholders inside fixed-width blocks and is
   visibility-hidden via .bedrock-countdown--pending until the first tick: the box is
   already the right size, so revealing it costs no layout shift, and no visitor
   ever sees a frozen row of zeros. A JS failure leaves the reserved gap empty —
   the banner's label, copy and code button all still render.

   Dates are parsed and compared in the VIEWER's timezone, not the shop's. For a
   fixed sale end that matters, put the offset in the date string (see the schema
   info text). */

(function () {
  var SECOND = 1000;
  var MINUTE = 60 * SECOND;
  var HOUR = 60 * MINUTE;
  var DAY = 24 * HOUR;

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  /* Next occurrence of the chosen interval, always strictly in the future. */
  function evergreenTarget(interval) {
    var now = new Date();
    var due = new Date(now.getTime());
    var step;

    switch (interval) {
      case 'every_month':
        due.setMonth(due.getMonth() + 1);
        due.setDate(1);
        due.setHours(0, 0, 0, 0);
        return due;
      case 'every_week':
        due.setDate(now.getDate() + (7 - now.getDay()));
        due.setHours(0, 0, 0, 0);
        return due;
      case 'every_2_hrs':
        step = 2;
        break;
      case 'every_6_hrs':
        step = 6;
        break;
      case 'every_12_hrs':
        step = 12;
        break;
      default:
        step = 24;
    }

    due.setHours(0, 0, 0, 0);
    /* setHours accepts 24 and rolls into tomorrow, which is what we want at the
       end of the day — no separate wrap-around branch. */
    due.setHours((Math.floor(now.getHours() / step) + 1) * step);
    return due;
  }

  function fixedTarget(value) {
    if (!value) return null;
    /* "2026-08-31 21:00" is not a format every engine parses; the T form is. */
    var parsed = new Date(value.trim().replace(' ', 'T'));
    if (isNaN(parsed.getTime())) parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  if (!customElements.get('bedrock-countdown')) {
    customElements.define(
      'bedrock-countdown',
      class extends HTMLElement {
        connectedCallback() {
          if (this.interval) return;

          this.nodes = {};
          ['day', 'hour', 'minute', 'second'].forEach(
            function (unit) {
              this.nodes[unit] = this.querySelector('[data-timer-' + unit + ']');
            }.bind(this)
          );

          if (!this.resolveTarget()) {
            this.hidden = true;
            return;
          }

          this.tick();
          this.interval = setInterval(this.tick.bind(this), SECOND);
        }

        disconnectedCallback() {
          clearInterval(this.interval);
          this.interval = null;
        }

        resolveTarget() {
          var due =
            this.dataset.mode === 'fixed'
              ? fixedTarget(this.dataset.dueDate)
              : evergreenTarget(this.dataset.interval);
          if (!due || due.getTime() <= Date.now()) return false;
          this.target = due.getTime();
          return true;
        }

        tick() {
          var remaining = this.target - Date.now();

          if (remaining <= 0) {
            if (this.dataset.mode !== 'fixed' && this.resolveTarget()) {
              remaining = this.target - Date.now();
            } else {
              this.disconnectedCallback();
              this.hidden = true;
              return;
            }
          }

          /* With the day block off, the hours field carries the whole remainder. */
          var showsDays = Boolean(this.nodes.day);

          this.write('day', showsDays ? Math.floor(remaining / DAY) : 0);
          this.write('hour', Math.floor((showsDays ? remaining % DAY : remaining) / HOUR));
          this.write('minute', Math.floor((remaining % HOUR) / MINUTE));
          this.write('second', Math.floor((remaining % MINUTE) / SECOND));

          this.classList.remove('bedrock-countdown--pending');
        }

        write(unit, value) {
          if (this.nodes[unit]) this.nodes[unit].textContent = pad(value);
        }
      }
    );
  }

  /* --- Discount code copy --- */

  function legacyCopy(text) {
    return new Promise(function (resolve, reject) {
      var scratch = document.createElement('textarea');
      scratch.value = text;
      scratch.setAttribute('readonly', '');
      scratch.style.position = 'fixed';
      scratch.style.opacity = '0';
      document.body.appendChild(scratch);
      scratch.select();
      var ok = document.execCommand('copy');
      scratch.remove();
      ok ? resolve() : reject(new Error('copy failed'));
    });
  }

  /* The Clipboard API can reject even in a secure context — a denied permission,
     a policy, an unfocused document. Falling through to the textarea trick on
     rejection is what keeps the button working there instead of doing nothing at
     all, silently. */
  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).catch(function () {
        return legacyCopy(text);
      });
    }
    return legacyCopy(text);
  }

  document.addEventListener('click', function (event) {
    var button = event.target instanceof Element && event.target.closest('[data-bedrock-promobar-copy]');
    if (!button) return;

    var wrapper = button.closest('.bedrock-promobar__code');
    var tooltip = wrapper && wrapper.querySelector('.bedrock-promobar__tooltip');
    var live = wrapper && wrapper.querySelector('.bedrock-promobar__status');

    copyText(button.dataset.bedrockPromobarCopy).then(function () {
      if (tooltip) tooltip.classList.add('bedrock-promobar__tooltip--visible');
      if (live) live.textContent = button.dataset.copiedAnnouncement || '';
      clearTimeout(button.bedrockPromobarTimeout);
      button.bedrockPromobarTimeout = setTimeout(function () {
        if (tooltip) tooltip.classList.remove('bedrock-promobar__tooltip--visible');
        if (live) live.textContent = '';
      }, 2000);
    }, function () {
      /* Both paths refused. The code is still on screen to select by hand. */
    });
  });
})();
